import { IComponent } from "./interfaces";

/**
 * Přidá komponentu do seznamu komponent picassa a zároveň updatuje reference potomků, reference rodičů a aktualizuje indexy.
 * @param parentIndex Index rodičovské komponenty.
 * @param mutablePicComponents Seznam všech komponent picassa.
 * @param picassoComponentCreator Funkce která tvoří novou instanci picasso komponenty.
 * @returns Objekt obsahující nové pole picasso komponent a index přidané komponenty.
 */
export const addPicassoComponent = (
    parentIndex: number,
    mutablePicComponents: IComponent[],
    picassoComponentCreator: (newComponentIndex: number, parentIndex: number) => IComponent,
): { updatedPicassoComponents: readonly IComponent[]; newComponentIndex: number } => {
    const newComponentIndex = getLastDescendantIndex(mutablePicComponents[parentIndex], mutablePicComponents) + 1;

    for (let i = 0; i < mutablePicComponents.length; i++) {
        const picassoComponent = mutablePicComponents[i];
        const updatedChildren = picassoComponent.children.map(child => child >= newComponentIndex ? child + 1 : child);
        const shouldMoveParent = picassoComponent.parent !== null && picassoComponent.parent >= newComponentIndex;

        mutablePicComponents[i] = {
            ...picassoComponent,
            index: i >= newComponentIndex ? picassoComponent.index + 1 : picassoComponent.index,
            parent: shouldMoveParent ? picassoComponent.parent + 1 : picassoComponent.parent,
            children: i === parentIndex ? [...updatedChildren, newComponentIndex] : updatedChildren,
        };
    }

    mutablePicComponents.splice(newComponentIndex, 0, picassoComponentCreator(newComponentIndex, parentIndex));

    return { updatedPicassoComponents: mutablePicComponents, newComponentIndex };
};

export const getLastDescendantIndex = (currentComponent: IComponent, picassoComponents: readonly IComponent[]): number => {
    if (currentComponent.children.length > 0) {
        return getLastDescendantIndex(picassoComponents[currentComponent.children[currentComponent.children.length - 1]], picassoComponents);
    }

    return currentComponent.index;
};

/**
 * Vrací pole indexů podstromů hlavního stromu. (Označené komponenty totiž mají ještě děti).
 * @param selectedComponents Komponenty označené uživatelem.
 * @param picassoComponents Seznam všech komponent.
 * @returns Pole, které obsahuje indexy komponent podstromu (včetně zvolených komponent).
 */
export const getComponentsSubtree = (selectedComponents: readonly number[], picassoComponents: readonly IComponent[]): number[] => {
    const refArray: number[] = [];

    const getComponentsRecursively = (componentsToGet: readonly number[]) => {
        for (const selectedComponent of componentsToGet) {
            const picassoComponent = picassoComponents[selectedComponent];

            refArray.push(picassoComponent.index);

            if (picassoComponent.children.length !== 0) {
                getComponentsRecursively(picassoComponent.children);
            }
        }
    };

    getComponentsRecursively(selectedComponents);

    return refArray;
};
